"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { trackEvent } from "@/components/analytics";

/**
 * Fires `blog_contextual_cta_click` for every conversion link inside a blog post.
 *
 * Blog CTAs were previously instrumented per post, which meant only two of the
 * 28 posts emitted the event at all — the twelve that render `BlogPostCta` were
 * silent, so per-post CTA performance could not be compared. Delegating at the
 * layout covers every post, including ones written later, without each author
 * having to remember to wire an event up.
 *
 * Links rendered by `TrackedLink` carry `data-tracked` and are skipped here so
 * the two already-instrumented posts keep emitting exactly one event per click.
 */

/** Destinations that represent a step toward activation or purchase. */
const CONVERSION_PATHS = ["/app/upload", "/app", "/pricing", "/login", "/signup"];

function conversionTarget(anchor: HTMLAnchorElement): string | null {
  // `href` is absolute once resolved by the DOM; only same-origin links count.
  const url = new URL(anchor.href, window.location.origin);
  if (url.origin !== window.location.origin) return null;
  const match = CONVERSION_PATHS.find(
    (path) => url.pathname === path || url.pathname.startsWith(`${path}/`)
  );
  return match ? url.pathname : null;
}

export function BlogCtaTracking() {
  const pathname = usePathname();

  useEffect(() => {
    // `/blog` itself is an index, not a post — nothing to attribute a CTA to.
    const slug = pathname?.startsWith("/blog/") ? pathname.slice("/blog/".length) : null;
    if (!slug) return;

    const handleClick = (event: MouseEvent) => {
      const anchor = (event.target as Element | null)?.closest?.("a");
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.closest("[data-tracked]")) return;

      const target = conversionTarget(anchor);
      if (!target) return;

      trackEvent("blog_contextual_cta_click", "conversion", slug, undefined, {
        post_slug: slug,
        cta_href: target,
        cta_text: anchor.textContent?.trim().slice(0, 80) ?? "",
        cta_in_nav: Boolean(anchor.closest("nav, header, footer")),
      });
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [pathname]);

  return null;
}
